// ============================================================================
// cg-macros.ts — catalogue de "macros" pédagogiques pour les blocs
// `constrained_geometry`. Une macro déplie une figure (triangle équilatéral,
// carré…) en points + contraintes intrinsèques + tracés, en un seul appel.
//
// PUR & DÉTERMINISTE (aucun Date.now / Math.random) → testable, et réutilisable
// par la future couche IA (`expandIntrinsics`, cf. docs/visual-constraint-engine.md).
//
// Décisions d'archi (doc §0/§4) :
//  - angle droit ⇒ contrainte `perpendicular` (JAMAIS `angle 90` : la projection
//    `angle` du solveur garde le signe courant et fait flipper la figure).
//  - SOUS-CONTRAINDRE : aucun point `fixed`, aucune distance absolue → l'échelle
//    reste un degré de liberté. Un triangle équilatéral reste donc équilatéral
//    quand on déplace/tourne/redimensionne (le solveur réimpose `equal_length`).
// ============================================================================

import type { CGConstraint, CGDrawable, CGPoint } from './visual-blocks';

export type CgMacroKind =
  | 'point'
  | 'segment'
  | 'triangle'
  | 'triangle_right'
  | 'triangle_isosceles'
  | 'triangle_equilateral'
  | 'square';

export interface CgMacroFragment {
  points: CGPoint[];
  constraints: CGConstraint[];
  drawables: CGDrawable[];
}

export interface CgMacroDef {
  kind: CgMacroKind;
  label: string;
  group: 'forme' | 'primitive';
}

/** Catalogue affiché dans le picker du formulaire (formes utiles d'abord). */
export const CG_MACROS: CgMacroDef[] = [
  { kind: 'triangle_equilateral', label: 'Triangle équilatéral', group: 'forme' },
  { kind: 'triangle_right', label: 'Triangle rectangle', group: 'forme' },
  { kind: 'triangle_isosceles', label: 'Triangle isocèle', group: 'forme' },
  { kind: 'triangle', label: 'Triangle quelconque', group: 'forme' },
  { kind: 'square', label: 'Carré', group: 'forme' },
  { kind: 'point', label: 'Point', group: 'primitive' },
  { kind: 'segment', label: 'Segment', group: 'primitive' },
];

export interface CgExisting {
  /** IDs déjà pris (points + contraintes + tracés) — pour éviter les collisions. */
  ids: ReadonlySet<string>;
  /** Noms de points déjà pris (A, B, …) — pour nommer les nouveaux sommets. */
  names: ReadonlySet<string>;
}

export interface CgExpandOptions {
  /** Décalage appliqué à tous les points générés (pour ne pas superposer les formes). */
  offset?: { x: number; y: number };
}

/** Alloue des noms de sommets libres : A, B, … Z, A1, B1, … */
function makeNameAllocator(used: ReadonlySet<string>): () => string {
  const taken = new Set(used);
  let i = 0;
  return () => {
    for (;;) {
      const letter = String.fromCharCode(65 + (i % 26));
      const round = Math.floor(i / 26);
      const name = round === 0 ? letter : `${letter}${round}`;
      i += 1;
      if (!taken.has(name)) {
        taken.add(name);
        return name;
      }
    }
  };
}

/** Alloue des IDs `${prefix}${n}` libres. Déterministe (compteur, pas d'horloge). */
function makeIdAllocator(used: ReadonlySet<string>): (prefix: string) => string {
  const taken = new Set(used);
  let n = 0;
  return (prefix: string) => {
    for (;;) {
      n += 1;
      const id = `${prefix}${n}`;
      if (!taken.has(id)) {
        taken.add(id);
        return id;
      }
    }
  };
}

const SQRT3 = Math.sqrt(3);

/**
 * Déplie une macro en fragment (points + contraintes + tracés), avec des IDs et
 * des noms de sommets FRAIS (aucune collision avec `existing`). Poses initiales
 * centrées sur l'origine, côté ~4. Aucun point `fixed` (DOF préservés).
 */
export function expandMacro(
  kind: CgMacroKind,
  existing: CgExisting,
  opts: CgExpandOptions = {},
): CgMacroFragment {
  const nextName = makeNameAllocator(existing.names);
  const nextId = makeIdAllocator(existing.ids);
  const ox = opts.offset?.x ?? 0;
  const oy = opts.offset?.y ?? 0;

  const mkPoint = (x: number, y: number): CGPoint => ({
    id: nextId('p'),
    name: nextName(),
    x: x + ox,
    y: y + oy,
  });
  const seg = (a: string, b: string): CGDrawable => ({ id: nextId('d'), kind: 'segment', a, b });
  const eqLen = (a: string, b: string, c: string, d: string): CGConstraint => ({
    id: nextId('c'),
    type: 'equal_length',
    a,
    b,
    c,
    d,
  });
  const perp = (a: string, b: string, c: string, d: string): CGConstraint => ({
    id: nextId('c'),
    type: 'perpendicular',
    a,
    b,
    c,
    d,
  });
  /** Tracé des côtés d'un polygone fermé (boucle). */
  const edges = (ids: string[]): CGDrawable[] =>
    ids.map((id, i) => seg(id, ids[(i + 1) % ids.length]!));

  switch (kind) {
    case 'point': {
      const p = mkPoint(0, 0);
      return { points: [p], constraints: [], drawables: [] };
    }
    case 'segment': {
      const a = mkPoint(-2, 0);
      const b = mkPoint(2, 0);
      return { points: [a, b], constraints: [], drawables: [seg(a.id, b.id)] };
    }
    case 'triangle': {
      // quelconque : 3 sommets non alignés, aucune contrainte (6 DOF).
      const a = mkPoint(-2.2, -1.5);
      const b = mkPoint(2, -1.8);
      const c = mkPoint(0.4, 2.2);
      return { points: [a, b, c], constraints: [], drawables: edges([a.id, b.id, c.id]) };
    }
    case 'triangle_right': {
      // angle droit au 1ᵉʳ sommet : AB ⟂ AC.
      const a = mkPoint(-1.5, -1.5);
      const b = mkPoint(2.5, -1.5);
      const c = mkPoint(-1.5, 2.5);
      return {
        points: [a, b, c],
        constraints: [perp(a.id, b.id, a.id, c.id)],
        drawables: edges([a.id, b.id, c.id]),
      };
    }
    case 'triangle_isosceles': {
      // apex au 1ᵉʳ sommet : |AB| = |AC|.
      const a = mkPoint(0, 2.5);
      const b = mkPoint(-2, -1.5);
      const c = mkPoint(2, -1.5);
      return {
        points: [a, b, c],
        constraints: [eqLen(a.id, b.id, a.id, c.id)],
        drawables: edges([a.id, b.id, c.id]),
      };
    }
    case 'triangle_equilateral': {
      // côté 4, centré sur le centroïde. 2× equal_length ⇒ 3 côtés égaux.
      const s = 4;
      const h = (s * SQRT3) / 2;
      const a = mkPoint(-s / 2, -h / 3);
      const b = mkPoint(s / 2, -h / 3);
      const c = mkPoint(0, (2 * h) / 3);
      return {
        points: [a, b, c],
        constraints: [eqLen(a.id, b.id, b.id, c.id), eqLen(b.id, c.id, c.id, a.id)],
        drawables: edges([a.id, b.id, c.id]),
      };
    }
    case 'square': {
      // côté 4. Losange (3× equal_length) + 1 angle droit ⇒ carré.
      const a = mkPoint(-2, -2);
      const b = mkPoint(2, -2);
      const c = mkPoint(2, 2);
      const d = mkPoint(-2, 2);
      return {
        points: [a, b, c, d],
        constraints: [
          eqLen(a.id, b.id, b.id, c.id),
          eqLen(b.id, c.id, c.id, d.id),
          eqLen(c.id, d.id, d.id, a.id),
          perp(a.id, b.id, b.id, c.id),
        ],
        drawables: edges([a.id, b.id, c.id, d.id]),
      };
    }
  }
}
